import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/Textarea";
import { Product } from "@/prisma-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Calendar,
  CheckIcon,
  ChevronsUpDownIcon,
  Clock,
  Edit,
  FileText,
  Loader2,
  Package2,
  Save,
  Tag,
} from "lucide-react";
import React, { FC } from "react";
import { toast } from "react-hot-toast";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/Card";
import { ComboboxSideDropdown } from "../../../components/ui/ComboboxSideDropdown";
// duplicate import removed
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/Command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import { Input } from "../../../components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/Select";
import { fetchWithAuth } from "../../../hooks/useAuth";
import { useCategory } from "../../../hooks/useCategory";
import { formatDate } from "../../../utils/format";

interface FormData {
  title: string;
  subtitle?: string;
  description?: string;
  status: "DRAFT" | "ACTIVE" | "ARCHIVED";
  vendor?: string;
  handle?: string;
  tags: string[];
}

export const ProductDetailsPannel: FC<{
  product: Product;
}> = ({ product }) => {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = React.useState(false);
  const [formData, setFormData] = React.useState<FormData>({
    title: product.title || product.name || "",
    subtitle: product.subtitle || "",
    description: product.description || "",
    status: product.status || "DRAFT",
    vendor: product.vendor || "",
    handle: product.handle || "",
    tags: Array.isArray(product.tags) ? product.tags : [],
  });
  const [handleTouched, setHandleTouched] = React.useState(false);
  const [initialHasHandle, setInitialHasHandle] = React.useState<boolean>(
    !!product.handle
  );
  const [tagInput, setTagInput] = React.useState("");
  const { categories, refetch } = useCategory({ fetchOnMount: true });

  const [selectedCategoryId, setSelectedCategoryId] = React.useState<string>(
    product.categories?.[0]?.category.id
      ? String(product.categories?.[0]?.category.id)
      : ""
  );
  const [selectedSubcategoryId, setSelectedSubcategoryId] =
    React.useState<string>(
      (product as any) && (product as any).subcategories?.[0]?.subcategory?.id
        ? String((product as any).subcategories?.[0]?.subcategory?.id)
        : ""
    );

  const slugify = (value: string) =>
    value
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)+/g, "");

  // Update form data when product changes
  React.useEffect(() => {
    setFormData({
      title: product.title || product.name || "",
      subtitle: product.subtitle || "",
      description: product.description || "",
      status: product.status || "DRAFT",
      vendor: product.vendor || "",
      handle: product.handle || "",
      tags: Array.isArray(product.tags) ? product.tags : [],
    });
    setHandleTouched(false);
    setInitialHasHandle(!!product.handle);
    setSelectedCategoryId(
      product.categories?.[0]?.category.id
        ? String(product.categories?.[0]?.category.id)
        : ""
    );
    setSelectedSubcategoryId(
      (product as any)?.subcategories?.[0]?.subcategory?.id
        ? String((product as any).subcategories?.[0]?.subcategory?.id)
        : ""
    );
  }, [product]);

  // Update product mutation
  const updateProduct = useMutation({
    mutationFn: async (data: FormData) => {
      const response = await fetchWithAuth(
        `/api/admin/products/${product.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: data.title.trim(),
            subtitle: data.subtitle?.trim() || null,
            description: data.description?.trim() || null,
            status: data.status,
            vendor: data.vendor?.trim() || null,
            handle: data.handle?.trim() || null,
            tags: data.tags,
          }),
        }
      );
      if (!response.ok) throw new Error("Eroare la actualizarea produsului");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["product", product.id.toString()],
      });
      toast.success("Produs actualizat cu succes");
      setIsEditing(false);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const updateCategories = useMutation({
    mutationFn: async (categoryId: string | "") => {
      const categoriesPayload = categoryId ? [Number(categoryId)] : [];
      const response = await fetchWithAuth(
        `/api/admin/products/${product.id}/categories`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ categories: categoriesPayload }),
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(
          err.message || "Eroare la actualizarea categoriei produsului"
        );
      }
      return response.json();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["product", product.id.toString()],
      });
      toast.success("Categoria produsului a fost actualizată");
    },
    onError: (e: any) => toast.error(e?.message || "Eroare necunoscută"),
  });

  const updateSubcategories = useMutation({
    mutationFn: async (subcategoryId: string | "") => {
      const payload = subcategoryId ? [Number(subcategoryId)] : [];
      const response = await fetchWithAuth(
        `/api/admin/products/${product.id}/subcategories`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ subcategories: payload }),
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(
          err.message || "Eroare la actualizarea subcategoriilor produsului"
        );
      }
      return response.json();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["product", product.id.toString()],
      });
      toast.success("Subcategoria produsului a fost actualizată");
    },
    onError: (e: any) => toast.error(e?.message || "Eroare necunoscută"),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProduct.mutate(formData, {
      onSuccess: () => {
        updateCategories.mutate(selectedCategoryId);
        updateSubcategories.mutate(selectedSubcategoryId);
      },
    });
  };

  return (
    <Card className="">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package2 className="w-5 h-5" />
          Detalii Produs
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="mb-2">Nume</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => {
                    const nextTitle = e.target.value;
                    setFormData((f) => ({
                      ...f,
                      title: nextTitle,
                      handle: initialHasHandle
                        ? f.handle // do not auto-change if product already has a handle
                        : f.handle && handleTouched
                        ? f.handle
                        : slugify(nextTitle),
                    }));
                  }}
                  placeholder="Nume produs"
                  required
                />
              </div>
              <div>
                <Label className="mb-2">Categorie</Label>
                <ComboboxSideDropdown
                  options={(categories || []).map((c: any) => ({
                    value: String(c.id),
                    label: c.name,
                    subOptions: Array.isArray((c as any).subcategories)
                      ? (c as any).subcategories.map((s: any) => ({
                          value: String(s.id),
                          label: s.name,
                        }))
                      : [],
                  }))}
                  valueParent={selectedCategoryId}
                  valueChild={selectedSubcategoryId}
                  onChange={(parent: string, child: string) => {
                    setSelectedCategoryId(parent);
                    setSelectedSubcategoryId(child);
                  }}
                  onOpenChange={(o: boolean) => {
                    if (o) refetch();
                  }}
                  buttonClassName="w-full"
                />
                <div className="text-xs text-muted-foreground mt-1">
                  {selectedSubcategoryId
                    ? `Subcategorie selectată: ${selectedSubcategoryId}`
                    : selectedCategoryId
                    ? `Categoria selectată: ${selectedCategoryId}`
                    : "Nicio categorie selectată"}
                </div>
              </div>
              <div>
                <Label className="mb-2">Subtitlu</Label>
                <Input
                  value={formData.subtitle || ""}
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, subtitle: e.target.value }))
                  }
                  placeholder="Subtitlu (opțional)"
                />
              </div>
              <div>
                <Label className="mb-2">Furnizor</Label>
                <Input
                  value={formData.vendor || ""}
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, vendor: e.target.value }))
                  }
                  placeholder="Numele furnizorului (opțional)"
                />
              </div>
              <div>
                <Label className="mb-2">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(v) =>
                    setFormData((f) => ({
                      ...f,
                      status: v as FormData["status"],
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Alege status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DRAFT">DRAFT</SelectItem>
                    <SelectItem value="ACTIVE">ACTIVE</SelectItem>
                    <SelectItem value="ARCHIVED">ARCHIVED</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="mb-2">URL Handle</Label>
                <Input
                  value={formData.handle || ""}
                  onChange={(e) => {
                    setHandleTouched(true);
                    setFormData((f) => ({
                      ...f,
                      handle: slugify(e.target.value),
                    }));
                  }}
                  placeholder="ex: titlu-produs"
                />
                <div className="text-xs text-muted-foreground mt-1">
                  URL: /product/{formData.handle || ""}
                </div>
              </div>
              <div className="flex flex-col gap-2 md:col-span-2">
                <Label className="mb-2">Descriere</Label>
                <Textarea
                  value={formData.description || ""}
                  className="min-h-[120px] max-h-[300px] resize-y "
                  onChange={(e) =>
                    setFormData((f) => ({
                      ...f,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Descriere produs&#10;&#10;Folosește Enter pentru o nouă linie&#10;Exemplu:&#10;- Carte de spiritualitate&#10;- Autor: Nume Autor&#10;- Anul publicării: 2024"
                  rows={5}
                />
              </div>
              <div className="md:col-span-2">
                <Label className="mb-5">Taguri</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.tags.map((tag, idx) => (
                    <Badge
                      key={`${tag}-${idx}`}
                      variant="outline"
                      className="group"
                    >
                      <span className="mr-1">{tag}</span>
                      <button
                        type="button"
                        className="opacity-60 group-hover:opacity-100 ml-1"
                        onClick={() =>
                          setFormData((f) => ({
                            ...f,
                            tags: f.tags.filter(
                              (t, i) => !(t === tag && i === idx)
                            ),
                          }))
                        }
                        aria-label={`Remove ${tag}`}
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
                <Input
                  className="mt-2"
                  placeholder="Scrie un tag și apasă Enter sau ,"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      const raw = tagInput.replace(",", " ").trim();
                      if (!raw) return;
                      const parts = raw
                        .split(/\s|,/) // split by spaces or commas
                        .map((p) => p.trim())
                        .filter(Boolean);
                      setFormData((f) => ({
                        ...f,
                        tags: Array.from(new Set([...f.tags, ...parts])),
                      }));
                      setTagInput("");
                    }
                  }}
                  onBlur={() => {
                    const raw = tagInput.trim();
                    if (!raw) return;
                    setFormData((f) => ({
                      ...f,
                      tags: Array.from(new Set([...f.tags, raw])),
                    }));
                    setTagInput("");
                  }}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditing(false)}
                className="flex items-center gap-1"
              >
                Anulează
              </Button>
              <Button
                type="submit"
                disabled={updateProduct.isPending}
                className="flex items-center gap-1 bg-green-600 hover:bg-green-700"
              >
                {updateProduct.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Se salvează...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Salvează
                  </>
                )}
              </Button>
            </div>
          </form>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <Tag className="w-4 h-4" />
                Nume
              </div>
              <div className="text-foreground">
                {product.title || product.name}
              </div>
            </div>
            {product.subtitle && (
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                  <FileText className="w-4 h-4" />
                  Subtitlu
                </div>
                <div className="text-foreground">{product.subtitle}</div>
              </div>
            )}
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <FileText className="w-4 h-4" />
                <Label className="mb-2">Descriere</Label>
              </div>
              <div className="text-foreground whitespace-pre-line">
                {product.description}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <Tag className="w-4 h-4" />
                Status
              </div>
              <div className="text-foreground">
                <Badge
                  variant={
                    product.status === "ACTIVE"
                      ? "success"
                      : product.status === "DRAFT"
                      ? "warning"
                      : "outline"
                  }
                >
                  {product.status}
                </Badge>
              </div>
            </div>
            {product.vendor && (
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                  <Tag className="w-4 h-4" />
                  Furnizor
                </div>
                <div className="text-foreground">{product.vendor}</div>
              </div>
            )}
            {product.handle && (
              <div className="md:col-span-2">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                  <Tag className="w-4 h-4" />
                  URL Handle
                </div>
                <div className="text-foreground break-all">
                  /product/{product.handle}
                </div>
              </div>
            )}
            {product.categories?.[0]?.category.id && (
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                  <Tag className="w-4 h-4" />
                  Categorie
                </div>
                <div className="text-foreground">
                  {(categories || []).find(
                    (c) => c.id === product.categories?.[0]?.category.id
                  )?.name || `#${product.categories?.[0]?.category.id}`}
                </div>
              </div>
            )}
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-5">
                <Tag className="w-4 h-4" />
                Taguri
              </div>
              <div className="flex flex-wrap gap-2">
                {product.tags && product.tags.length > 0 ? (
                  product.tags.map((t, i) => (
                    <Badge key={`${t}-${i}`} variant="outline">
                      {t}
                    </Badge>
                  ))
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <Calendar className="w-4 h-4" />
                Data Adăugării
              </div>
              <div className="text-foreground">
                {formatDate(product.createdAt)}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <Clock className="w-4 h-4" />
                Ultima Actualizare
              </div>
              <div className="text-foreground">
                {formatDate(product.updatedAt)}
              </div>
            </div>
            <div className="col-span-2">
              <Button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-1"
              >
                <Edit className="w-4 h-4" />
                Editează
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// legacy picker kept for reference but not used
function CategorySubcategoryPicker(props: {
  categories: Array<{ id: number; name: string }>;
  valueCategoryId: string;
  valueSubcategoryId: string;
  onChange: (categoryId: string, subcategoryId: string) => void;
  onOpen?: () => void;
}) {
  const { categories, valueCategoryId, valueSubcategoryId, onChange, onOpen } =
    props;
  const [open, setOpen] = React.useState(false);
  const [subcategories, setSubcategories] = React.useState<
    Array<{ id: number; name: string }>
  >([]);
  const [loadingSubs, setLoadingSubs] = React.useState(false);

  const currentCategory = categories.find(
    (c) => String(c.id) === valueCategoryId
  );

  const loadSubcategories = async (categoryId: string) => {
    if (!categoryId) return setSubcategories([]);
    try {
      setLoadingSubs(true);
      const res = await fetchWithAuth(
        `/api/admin/categories/${categoryId}/subcategories`
      );
      const json = await res.json().catch(() => []);
      const list = Array.isArray(json)
        ? json
        : Array.isArray(json?.data)
        ? json.data
        : Array.isArray(json?.date)
        ? json.date
        : [];
      setSubcategories(list.map((s: any) => ({ id: s.id, name: s.name })));
    } finally {
      setLoadingSubs(false);
    }
  };

  React.useEffect(() => {
    if (valueCategoryId) {
      loadSubcategories(valueCategoryId);
    } else {
      setSubcategories([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueCategoryId]);

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) {
          onOpen?.();
          if (valueCategoryId) {
            // ensure subcategories are loaded when opening
            loadSubcategories(valueCategoryId);
          }
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          className="w-full justify-between"
        >
          {valueSubcategoryId
            ? subcategories.find((s) => String(s.id) === valueSubcategoryId)
                ?.name ||
              currentCategory?.name ||
              "Selectează categoria"
            : currentCategory?.name || "Selectează categoria"}
          <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-0">
        <Command>
          <CommandInput placeholder="Caută..." />
          <CommandList>
            <CommandEmpty>Fără rezultate</CommandEmpty>
            <CommandGroup heading="Categorii">
              {categories.map((c) => (
                <CommandItem
                  key={`cat-${c.id}`}
                  onSelect={() => {
                    const id = String(c.id);
                    onChange(id, "");
                  }}
                >
                  <CheckIcon
                    className={
                      "mr-2 h-4 w-4 " +
                      (String(c.id) === valueCategoryId
                        ? "opacity-100"
                        : "opacity-0")
                    }
                  />
                  {c.name}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandGroup heading="Subcategorii">
              {loadingSubs ? (
                <div className="py-3 text-center text-sm text-muted-foreground">
                  Se încarcă…
                </div>
              ) : subcategories.length === 0 ? (
                <div className="py-3 text-center text-sm text-muted-foreground">
                  Nu există subcategorii
                </div>
              ) : (
                subcategories.map((s) => (
                  <CommandItem
                    key={`sub-${s.id}`}
                    onSelect={() => onChange(valueCategoryId, String(s.id))}
                  >
                    <CheckIcon
                      className={
                        "mr-2 h-4 w-4 " +
                        (String(s.id) === valueSubcategoryId
                          ? "opacity-100"
                          : "opacity-0")
                      }
                    />
                    {s.name}
                  </CommandItem>
                ))
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
